import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
} from "react"
import { useHistory } from "react-router-dom"
import { Helmet } from "react-helmet"
import debounce from "lodash/debounce"
import "intersection-observer"
import classnames from "classnames"
import DataLoader, { years } from "../DataLoader"
import { updatePaths, updateTransform } from "../Map"
import MapRenderer from "../Map/MapRenderer"
import { GradientLegend, PercentChangeLegend } from "../Legends/"
import gradient from "../utils/gradient"
import { getCountryByCode } from "../utils/countryLookup.js"
import ExploreButton from "./ExploreButton"

import "./styles.scss"

const emptyArray = []

const currentYear = years[years.length - 1]
const lastYear = years[years.length - 2]

let measured = false
let fromTransform, toTransform
const smallWidth = 768
let smallWidthMode = document.body.clientWidth < smallWidth

const padding = 46

export const percentChangeColorScale = (value) => {
  if (value === null) {
    return "rgb(51, 51, 51)"
  }
  if (value <= -4) {
    return "#D12341"
  } else if (value <= -2) {
    return "#EB9727"
  } else if (value <= 0) {
    return "#F4CD2E"
  } else if (value <= 2) {
    return "#90D1EB"
  } else if (value <= 4) {
    return "#2879BD"
  } else {
    return "#2D3589"
  }
}

const colors = {
  background: "#f2f2f2",
  defaultFill: "#ddd",
}

function updateData(paths, year, priorYear, gradientScale) {
  const data = DataLoader.dataByYear[year].countries.map((c) => {
    return c
  })

  return paths.map((country) => {
    // country.info = getCountryById(country.id) || false;
    country.info = getCountryByCode(country.properties.WB_A3) || false
    country.data = false
    country.color = "#333333"
    country.percentDifferenceColor = "#333333"
    if (country.info) {
      const countryData = data.find(
        (c) => c["Country Code"] === country.info.code,
      )
      if (countryData) {
        country.data = countryData
        country.value = country.data.overall
        country.rank = country.data.overall_Rank_Global
        country.count = country.data.Count_Global
        country.externalPercentChange = country.data.externalPercentChange
        country.color = gradientScale(country.value)
        country.percentDifferenceColor = percentChangeColorScale(
          country.externalPercentChange,
        )
        country.radius = padding / 2
      } else {
        //console.warn(`no data for ${country.properties.WB_A3} ???`)
      }
    }
    return country
  })
}

function matchTransform() {
  fromTransform = measured ? toTransform.slice() : null
}

export default function Overview(props) {
  const { dataLoaded } = props

  // Before the data loads, this page renders in the same pass as the nav, so
  // the nav's ref isn't set on the very first render. Render once more before
  // the first paint to pick up its real height.
  const [, setNavMeasured] = useState(false)
  useLayoutEffect(() => setNavMeasured(true), [])
  const navHeight = Math.floor(
    props.navRef.current?.getBoundingClientRect().height || 80,
  )

  const history = useHistory()

  const [locationKey, setLocationKey] = useState()
  const legendContainer = useRef()
  const { location } = history
  useEffect(() => {
    if (location.key !== locationKey) {
      window.scrollTo(0, 0)
      setLocationKey(location.key)
    }
  }, [location, locationKey])

  const [metrics, setMetrics] = useState({
    width: document.body.clientWidth,
    height: window.innerHeight - navHeight,
    baseWidth: 1280,
    baseHeight: 800,
    activeWidth: 1280,
    activeHeight: 800,
    scale: 1,
  })

  const { baseWidth, baseHeight } = metrics
  const { setFade } = props

  // Measure before the first paint so the headline doesn't move afterwards
  useLayoutEffect(() => {
    function _measure() {
      const width = document.body.clientWidth
      let height = window.innerHeight - navHeight
      smallWidthMode = false
      if (width < smallWidth) {
        smallWidthMode = true
        height /= 2
      }

      const scale = Math.min(width / baseWidth, height / baseHeight)
      const activeWidth = width / scale
      const activeHeight = height / scale

      measured = true
      setMetrics({
        width,
        height,
        baseWidth,
        baseHeight,
        activeWidth,
        activeHeight,
        scale,
      })
    }

    _measure()

    const debouncedMeasure = debounce(_measure, 100, {
      leading: true,
      trailing: true,
    })
    window.addEventListener("resize", debouncedMeasure)
    return function cleanup() {
      window.removeEventListener("resize", debouncedMeasure)
    }
  }, [baseWidth, baseHeight, navHeight, history, setFade])

  const { width, height, activeWidth, activeHeight } = metrics

  const [colorByData, setColorByData] = useState(false)
  const slideData = useMemo(
    () => ({
      selectedRegion: emptyArray,
      colorByData,
    }),
    [colorByData],
  )
  const { selectedRegion = false } = slideData

  // When the data arrives, the map is drawn grey and hidden. On the frame
  // after that has painted, fade it in and color it, and let visitors in.
  // On a later visit the data is already there, so the button starts ready.
  const [mapShown, setMapShown] = useState(false)
  const [loadedOnArrival] = useState(dataLoaded)
  useEffect(() => {
    if (!dataLoaded) return undefined
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        matchTransform()
        setColorByData(true)
        setMapShown(true)
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [dataLoaded])
  const exploreReady = mapShown || loadedOnArrival

  const transform = useMemo(() => {
    return updateTransform(
      activeWidth,
      activeHeight - navHeight,
      selectedRegion,
    )
    // dataLoaded: the world map arrives with the data and changes the projection
  }, [activeWidth, activeHeight, navHeight, selectedRegion, dataLoaded])
  const useWidth = useMemo(
    () =>
      transform.dx / activeWidth > transform.dy / (activeHeight - navHeight),
    [transform, activeWidth, activeHeight, navHeight],
  )
  toTransform = [
    transform.x,
    transform.y,
    useWidth ? transform.dx : transform.dy,
    useWidth ? activeWidth : activeHeight - navHeight,
  ]
  fromTransform = measured
    ? fromTransform || toTransform.slice()
    : toTransform.slice()

  const updatedPaths = useMemo(
    () => (dataLoaded ? updatePaths(transform) : emptyArray),
    [transform, dataLoaded],
  )
  const paths = useMemo(
    () =>
      dataLoaded
        ? updateData(updatedPaths, currentYear, lastYear, gradient)
        : emptyArray,
    [updatedPaths, dataLoaded],
  )

  const gradientLegendHeight = 44

  return (
    <div className={`Overview`}>
      <Helmet>
        <title>WJP Rule of Law Index</title>
        <meta
          name="description"
          content={`Explore global rankings in the ${currentYear} WJP Rule of Law Index. Original data on human rights, access to justice, corruption & authoritarianism in 143 countries.`}
        />
      </Helmet>
      <div className={`scrollContainer ${smallWidthMode ? "small" : ""}`}>
        <div
          className="stickyContainer"
          style={{
            background: colors.background,
            top: navHeight,
            zIndex: smallWidthMode ? 10 : 0,
            width,
            height,
          }}
        >
          <div
            className="scaleContainer"
            style={{
              width: activeWidth,
              height: activeHeight,
              transform: `scale(${metrics.scale})`,
            }}
          >
            <div
              className={classnames("overviewMap", { shown: mapShown })}
              style={{ pointerEvents: "none" }}
            >
              <MapRenderer
                width={activeWidth}
                height={activeHeight}
                colorByData={slideData.colorByData}
                colorByPercentDifferenceData={
                  slideData.colorByPercentDifferenceData
                }
                paths={paths}
                selectedLabels={slideData.selectedLabels}
                defaultStroke={colors.background}
                defaultFill={colors.defaultFill}
                countryOpacity={1}
                labelOpacity={1}
                pointOpacity={0.2}
                scale={0.2}
                tooltipEnabled={true}
                translate={transform.translate}
                showPercentDifferenceLabel={
                  slideData.showPercentDifferenceLabel
                    ? slideData.showPercentDifferenceLabel
                    : false
                }
              />
            </div>

            <div
              className="legend"
              style={{
                opacity: 1,
                top: 0,
                right: smallWidthMode ? 0 : padding,
                left: smallWidthMode
                  ? slideData.colorByPercentDifferenceData
                    ? "27%"
                    : "50%"
                  : null,
                transform: smallWidthMode
                  ? `scale(${(width - padding) / 2 / 320 / metrics.scale})`
                  : null,
              }}
            >
              <div
                className="legendContainer"
                style={{
                  marginLeft: smallWidthMode ? padding / 4 : null,
                  width: slideData.colorByPercentDifferenceData ? 513 : null,
                }}
                ref={legendContainer}
              >
                {slideData.colorByPercentDifferenceData ? (
                  <PercentChangeLegend
                    percentChangeScale={percentChangeColorScale}
                    width={490}
                    height={gradientLegendHeight}
                  />
                ) : (
                  <GradientLegend width={320} height={gradientLegendHeight} />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="overviewModal visible">
          <div className="modalContent">
            <h1>The Global Rule of Law Recession Continues</h1>
            <div>
              The {currentYear} <i>WJP Rule of Law Index</i>
              <sup>
                <small>®</small>
              </sup>{" "}
              evaluates 143 countries and jurisdictions around the world. For
              the ninth year in a row, the rule of law has declined in most
              countries.
            </div>
          </div>
          <ExploreButton ready={exploreReady} />
        </div>
      </div>
    </div>
  )
}
